import { AgentV2UIStateContext } from './agent-v2-ui-state-context'
import type { AgentV2Message } from '@/agent-v2/store/reducer'
import { isValidUuid } from '@/util/is-valid-uuid'
import type { AgentV2LinkRenderer } from './AgentV2Markdown'
import { useContext, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type MutableRefObject } from 'react'
import { DocxExportAnnouncer } from '@/agent-v2/docx-export/components/DocxExportAnnouncer'
import { withoutSettledDocxExportStarts } from '@/agent-v2/docx-export/utils/without-settled-docx-export-starts'
import { useConversationLawStatus } from '@/agent-v2/hooks/useConversationLawStatus'
import { useLocation, useParams, type NavigateOptions } from 'react-router-dom'
import { useStore } from 'react-redux'
import { RootState } from '@/store/store'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { getAgentV2MatterSubmitBlockedReason, kAgentV2MaxTurnInputChars } from '@/agent-v2/utils/agent-v2-contract'
import { ArtifactEditorEntryPoint } from '@/analytics/enums/artifact-editor.enums'
import { artifactEditorNavigationState } from '@/analytics/utils/artifact-editor-entry-analytics'
import { clearDictationUsed } from '@/analytics/utils/dictation-analytics'
import { useOpenAgentV2FileLink } from '@/agent-v2/hooks/useOpenAgentV2FileLink'
import { useFollowStreamScroll } from '@/agent-v2/hooks/useFollowStreamScroll'
import { useOpenMatterArtifact } from '@/agent-v2/hooks/useOpenMatterArtifact'
import { getAgentSearchDefaults } from '@/agent-v2/hooks/useAgentSearchDefaults'
import { getTurnAddedFilesByMessageId } from '@/agent-v2/utils/get-turn-added-files-by-message-id'
import type { AgentV2ResponsePresentation } from '@/agent-v2/components/AgentV2MessageBubble'
import { AgentV2ChatMessage } from '@/agent-v2/components/AgentV2ChatMessage'
import ViewOnlyChatNotice from '@/agent-v2/components/ViewOnlyChatNotice'
import ScrollToBottomButton from '@/agent-v2/components/ScrollToBottomButton'
import { Button } from '@/components/button/Button'
import AgentV2ChatComposer from '@/agent-v2/components/AgentV2ChatComposer'
import { AgentV2StaleLawBanner } from '@/agent-v2/components/AgentV2StaleLawBanner'
import ChatWindowUserFilesUpload, { ChatWindowUserFilesUploadRef } from '@/chat-common/components/ChatWindowUserFilesUpload'
import { useChatFileDropzone } from '@/chat-common/hooks/useChatFileDropzone'
import { DropFilesToUploadLabel } from '@/uppy-file-upload/components/DropFilesToUploadLabel'
import { FileUploadContext } from '@/context/file-upload-context'
import { selectAgentV2NextTurnHasUnreadyFiles } from '@/agent-v2/chat-form/store/selectors'
import { AgentConversationFormActions } from '@/agent-v2/chat-form/store/slice'
import { AgentV2ConversationsActions, AgentV2RunPhase } from '@/agent-v2/conversations/store/slice'
import {
  selectAgentV2ConversationFirstLoadPending,
  selectAgentV2ConversationFromId,
  selectAgentV2ConversationHistory,
  selectAgentV2RunPhase,
  selectAgentV2StopRequested,
} from '@/agent-v2/conversations/store/selectors'
import type { AgentV2TurnFeedback } from '@/agent-v2/feedback/schemas'
import { useIsAgentV2ConversationCreator } from '@/agent-v2/conversations/hooks/useIsAgentV2ConversationCreator'
import { useCreateNewAgentV2Chat } from '@/agent-v2/conversations/hooks/useNewAgentV2Chat'
import { kDefaultFaviconPath } from '@/constants/constants-ui'
import { selectAgentV2LatestMatterArtifactVersionIds, selectAgentV2Transcript, selectAgentV2UserMessages } from '@/agent-v2/store/selectors'
import { AgentV2TransportActions } from '@/agent-v2/transport/store/slice'
import { selectAgentV2TransportCanStopOrAbort, selectAgentV2TransportIsDetached } from '@/agent-v2/transport/store/selectors'
import type { AgentV2SearchDefaults, MatterArtifactVersionSelection } from '@/agent-v2/store/schemas'
import type { MatterArtifactVersionRead } from '@/matter-artifacts/schemas'
import { selectMatterArtifactVersionById, selectOpenMatterArtifactId } from '@/matter-artifacts/store/selectors'
import { useDraftWorkspace } from '@/drafting/workspace/hooks/useDraftWorkspace'
import { useIsTouchOnlyDevice } from '@/hooks/useIsTouchOnlyDevice'
import { conversationHasAttachedSkills, resolveAttachedSkillDisplayNames, resolveComposerSubmitText } from '@/user-skills/utils/resolve-composer-submit-text'

export type AgentV2SubmissionOverrides = {
  preparedInputText?: string
  clientRequestId?: string
  searchDefaults?: AgentV2SearchDefaults
  /* Resends an earlier turn as it was, so it doesn't change the chat's files or the composer */
  replaysEarlierTurn?: boolean
}
export type AgentV2Submit = (text: string, overrides?: AgentV2SubmissionOverrides) => Promise<boolean>

type AgentV2ChatWindowProps = {
  openTableId?: string
  renderResponseFooter?: (message: AgentV2Message) => ReactNode
  renderLink?: AgentV2LinkRenderer
  conversationId: string
  emptyStateHeading?: string
  emptyStateActions?: ReactNode
  emptyStateContent?: ReactNode
  inputPlaceholder?: string
  onDownloadConversation?: () => Promise<void>
  onOpenMatterSettings?: (matterId: string) => void
  onOpenGlobalSettings?: () => void
  onOpenExternalArtifact?: (versionId: string) => void
  responsePresentation?: AgentV2ResponsePresentation
  compactProgress?: boolean
  formatUserMessage?: (text: string) => string
  showReferenceList?: boolean
  showVoiceInput?: boolean
  prepareSubmission?: (text: string) => Promise<string>
  submitRef?: MutableRefObject<AgentV2Submit | null>
  composerActions?: ReactNode
  /** Replace the composer while the embedding host lacks required context, keeping the chat mounted. */
  composerUnavailableContent?: ReactNode
  submissionBlockedReason?: string | null
}

export default function AgentV2ChatWindow({
  openTableId,
  conversationId,
  renderLink,
  renderResponseFooter,
  emptyStateHeading = 'What are you working on today?',
  emptyStateActions,
  emptyStateContent,
  inputPlaceholder,
  onDownloadConversation,
  onOpenMatterSettings,
  onOpenGlobalSettings,
  onOpenExternalArtifact,
  responsePresentation,
  compactProgress,
  formatUserMessage,
  showReferenceList,
  showVoiceInput = true,
  prepareSubmission,
  submitRef,
  composerActions,
  composerUnavailableContent,
  submissionBlockedReason,
}: AgentV2ChatWindowProps) {
  const { matterId } = useParams()
  const appDispatch = useAppDispatch()
  const conversation = useAppSelector((state: RootState) => selectAgentV2ConversationFromId(state, { conversationId }))
  const runPhase = useAppSelector((state: RootState) => selectAgentV2RunPhase(state, { conversationId }))
  const canStopOrAbort = useAppSelector((state: RootState) => selectAgentV2TransportCanStopOrAbort(state, { conversationId }))
  const transportIsDetached = useAppSelector((state: RootState) => selectAgentV2TransportIsDetached(state, { conversationId }))
  const transcript = useAppSelector((state: RootState) => selectAgentV2Transcript(state, { conversationId }))
  const visibleMessages = useMemo(() => withoutSettledDocxExportStarts(transcript.messages), [transcript.messages])
  const userMessages = useAppSelector((state: RootState) => selectAgentV2UserMessages(state, { conversationId }))
  const turnAddedFilesByMessageId = useMemo(() => getTurnAddedFilesByMessageId(userMessages), [userMessages])

  const sharedStopRequested = useAppSelector((state: RootState) => selectAgentV2StopRequested(state, { conversationId }))
  const isConversationCreator = useIsAgentV2ConversationCreator(conversationId)
  const conversationMatterId = conversation?.matter_id ?? matterId
  const creatorDisplayName = conversation?.creator_display_name ?? null
  const { createNewAgentV2Chat } = useCreateNewAgentV2Chat()
  const selectedSkillIds = useAppSelector((state) => state.userSkillSelectionState.byConversationId[conversationId]?.selectedSkillIds)
  const selectedPaxtonSkillNames = useAppSelector((state) => state.userSkillSelectionState.byConversationId[conversationId]?.selectedPaxtonSkillNames)
  const skillLibrarySkills = useAppSelector((state) => state.userSkillLibraryState.skills)
  const skillLibraryPaxtonSkills = useAppSelector((state) => state.userSkillLibraryState.paxtonSkills)
  const [preparingRequest, setPreparingRequest] = useState(false)
  const [prepareError, setPrepareError] = useState<string | null>(null)
  const preparingRequestRef = useRef(false)
  const requestScopeRef = useRef(0)
  useEffect(() => {
    requestScopeRef.current += 1
    return () => {
      requestScopeRef.current += 1
    }
  }, [conversationId, matterId])
  const [isCreatingOwnChat, setIsCreatingOwnChat] = useState(false)
  const [createOwnChatError, setCreateOwnChatError] = useState(false)
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null)
  const runControlButtonRef = useRef<HTMLButtonElement | null>(null)
  const wasRunningRef = useRef(false)
  const chatWindowUserFilesUploadRef = useRef<ChatWindowUserFilesUploadRef | null>(null)
  const chatWindowWritingSampleUploadRef = useRef<ChatWindowUserFilesUploadRef | null>(null)
  // The chat-input-box wrapper keeps its DOM node across the empty→docked layout flip (explicit
  // key below), so its rect can be compared before and after to drive the first-message animation.
  const chatInputBoxRef = useRef<HTMLDivElement | null>(null)
  // Input-box position captured at submit time, so only a real first-message submit animates the
  // composer down to the bottom — loading an existing conversation never does (FE-555).
  const firstMessageFlipFromRectRef = useRef<DOMRect | null>(null)

  const matterSubmitBlockedReason = getAgentV2MatterSubmitBlockedReason(matterId)
  const store = useStore<RootState>()
  const hasUnreadyUserFiles = useAppSelector((appState: RootState) => selectAgentV2NextTurnHasUnreadyFiles(appState, { conversationId }))
  const conversationSubmitBlockedReason = submissionBlockedReason ?? (isValidUuid(conversationId) ? null : 'Conversation is still loading.')

  const openSystemUserFilesFileSelector = () => {
    chatWindowUserFilesUploadRef.current?.openSystemFileSelector()
  }
  const openSystemWritingSampleFileSelector = () => {
    chatWindowWritingSampleUploadRef.current?.openSystemFileSelector()
  }
  const cancelUserFileUpload = (clientTmpId: string) => {
    chatWindowUserFilesUploadRef.current?.cancelUpload(clientTmpId)
    chatWindowWritingSampleUploadRef.current?.cancelUpload(clientTmpId)
  }
  const userFilesUploadDisabledReason = conversationMatterId ? undefined : 'Matter is still loading.'
  const openFileLink = useOpenAgentV2FileLink(conversationId)
  const openMatterArtifact = useOpenMatterArtifact(onOpenExternalArtifact)
  const openMatterArtifactFromChat = useCallback(
    (matterArtifactVersion: MatterArtifactVersionRead, navigateOptions?: NavigateOptions) => {
      openMatterArtifact(matterArtifactVersion, {
        ...navigateOptions,
        state: artifactEditorNavigationState(ArtifactEditorEntryPoint.Chat, navigateOptions?.state),
      })
    },
    [openMatterArtifact]
  )
  const { draftArtifactVersionId } = useDraftWorkspace()
  const uiState = useContext(AgentV2UIStateContext)
  const openMatterArtifactId = useAppSelector((state: RootState) => selectOpenMatterArtifactId(state, { draftArtifactVersionId }))
  const { scrollToMatterArtifactVersionId: requestedScrollMatterArtifactVersionId } = (useLocation().state ?? {}) as {
    scrollToMatterArtifactVersionId?: string
  }
  const [scrollToMatterArtifactVersionId, setScrollToMatterArtifactVersionId] = useState<string | null>(null)
  const latestMatterArtifactVersionIds = useAppSelector((state: RootState) => selectAgentV2LatestMatterArtifactVersionIds(state, { conversationId }))
  const scrollToMatterArtifactId = useAppSelector(
    (state: RootState) => selectMatterArtifactVersionById(state, scrollToMatterArtifactVersionId)?.matter_artifact_id ?? null
  )
  const matterArtifactVersionsById = useAppSelector((state: RootState) => state.matterArtifactsState.versionsById)
  const lifecycleRunIsActive = runPhase === AgentV2RunPhase.SUBMITTING || runPhase === AgentV2RunPhase.RUNNING || runPhase === AgentV2RunPhase.STOPPING
  const isRecovering = runPhase === AgentV2RunPhase.RECOVERING
  const isRunning = lifecycleRunIsActive || canStopOrAbort
  const hasStaleLaw = useConversationLawStatus(conversationId, isRunning)
  const isStopping = runPhase === AgentV2RunPhase.STOPPING || sharedStopRequested
  // A turn sent before the first load would leave out the chat's files, which that load is still restoring
  const isFirstLoadPending = useAppSelector((state: RootState) => selectAgentV2ConversationFirstLoadPending(state, { conversationId }))

  const handleChatFilesDropped = useCallback((files: File[]) => {
    chatWindowUserFilesUploadRef.current?.addFiles(files)
  }, [])

  const { isDraggingFilesOver: isDraggingFilesOverChat, dragHandlers: chatFileDragHandlers } = useChatFileDropzone({
    disabled: !isConversationCreator || !!userFilesUploadDisabledReason || isRunning,
    onFilesDropped: handleChatFilesDropped,
  })

  const hasAttachedSkills = conversationHasAttachedSkills({ selectedSkillIds, selectedPaxtonSkillNames })
  const attachedSkillDisplayNames = useMemo(
    () =>
      resolveAttachedSkillDisplayNames({ selectedSkillIds, selectedPaxtonSkillNames }, { skills: skillLibrarySkills, paxtonSkills: skillLibraryPaxtonSkills }),
    [selectedSkillIds, selectedPaxtonSkillNames, skillLibrarySkills, skillLibraryPaxtonSkills]
  )

  // Expects already-resolved composer text (or null). Callers resolve once via resolveComposerSubmitText.
  const canSubmitResolvedText = useCallback(
    (resolvedText: string | null): resolvedText is string => {
      if (resolvedText === null) return false
      return (
        isConversationCreator &&
        resolvedText.length <= kAgentV2MaxTurnInputChars &&
        !isRunning &&
        !isRecovering &&
        !isFirstLoadPending &&
        !matterSubmitBlockedReason &&
        !conversationSubmitBlockedReason &&
        !hasUnreadyUserFiles
      )
    },
    [conversationSubmitBlockedReason, hasUnreadyUserFiles, isConversationCreator, isFirstLoadPending, isRecovering, isRunning, matterSubmitBlockedReason]
  )

  const handleFeedbackChange = useCallback(
    (feedback: AgentV2TurnFeedback) => {
      appDispatch(AgentV2ConversationsActions.setTurnFeedback({ feedback }))
    },
    [appDispatch]
  )

  const sendStop = useCallback(() => {
    appDispatch(AgentV2TransportActions.stopTurnRequested({ conversationId }))
  }, [appDispatch, conversationId])

  const submitText = useCallback(
    async (text: string, matterArtifactSelection?: MatterArtifactVersionSelection, overrides?: AgentV2SubmissionOverrides): Promise<boolean> => {
      const resolvedText = resolveComposerSubmitText(text, attachedSkillDisplayNames, hasAttachedSkills)
      if (!canSubmitResolvedText(resolvedText)) return false
      if (!isValidUuid(matterId)) return false
      if (preparingRequestRef.current) return false
      preparingRequestRef.current = true
      setPreparingRequest(true)
      setPrepareError(null)
      const requestScope = requestScopeRef.current
      try {
        const inputText = overrides?.preparedInputText ?? (prepareSubmission ? await prepareSubmission(resolvedText) : resolvedText)
        if (!canSubmitResolvedText(inputText)) throw new Error('The prepared request is too long. Shorten your message and try again.')
        if (requestScope !== requestScopeRef.current) return false
        // prepareSubmission may attach files, so the turn's files are read from the store now
        // rather than from the render this callback was created in.
        const preparedState = store.getState()
        if (selectAgentV2NextTurnHasUnreadyFiles(preparedState, { conversationId })) {
          throw new Error('Wait for the attached files to finish processing, then try again.')
        }
        firstMessageFlipFromRectRef.current = openTableId ? null : chatInputBoxRef.current?.getBoundingClientRect() ?? null
        const clientRequestId = overrides?.clientRequestId ?? crypto.randomUUID()

        appDispatch(
          AgentV2TransportActions.startTurnRequested({
            assistantMessageId: crypto.randomUUID(),
            clientRequestId,
            conversationId,
            inputText,
            matterId,
            matterArtifactSelection,
            uiState,
            searchDefaults: overrides?.searchDefaults ?? getAgentSearchDefaults(preparedState, conversationId),
            replaysEarlierTurn: overrides?.replaysEarlierTurn,
            selectedSkillIds,
            selectedPaxtonSkillNames,
            userMessageId: crypto.randomUUID(),
          })
        )
        return true
      } catch (error) {
        setPrepareError(error instanceof Error ? error.message : 'Could not prepare this request. Please try again.')
        return false
      } finally {
        preparingRequestRef.current = false
        setPreparingRequest(false)
      }
    },
    [
      appDispatch,
      attachedSkillDisplayNames,
      canSubmitResolvedText,
      conversationId,
      hasAttachedSkills,
      matterId,
      openTableId,
      uiState,
      prepareSubmission,
      selectedPaxtonSkillNames,
      selectedSkillIds,
      store,
    ]
  )

  useEffect(() => {
    if (!submitRef) return
    submitRef.current = (text, overrides) => submitText(text, undefined, overrides)
    return () => {
      submitRef.current = null
    }
  }, [submitRef, submitText])

  const handleFollowUpQuestionClick = useCallback(
    async (question: string) => {
      clearDictationUsed(conversationId)
      appDispatch(AgentConversationFormActions.setInputText({ conversationId, text: question }))
      if (await submitText(question)) appDispatch(AgentConversationFormActions.clearSubmittedInputText({ conversationId, submittedText: question }))
    },
    [appDispatch, conversationId, submitText]
  )

  const {
    containerRef,
    contentRef,
    handleScroll,
    handleUserScrollInput,
    showScrollToBottomButton,
    scrollToBottom,
    scrollElementIntoView,
    prepareForPrepend,
    restoreAfterPrepend,
  } = useFollowStreamScroll()
  const history = useAppSelector((state: RootState) => selectAgentV2ConversationHistory(state, { conversationId }))
  const messageListRef = useRef<HTMLDivElement | null>(null)
  const [historyFitsViewport, setHistoryFitsViewport] = useState(false)
  useLayoutEffect(() => {
    const container = containerRef.current
    const content = messageListRef.current
    if (!container || !content || !history?.nextCursor) return
    const measure = () => setHistoryFitsViewport(container.clientHeight > 0 && container.scrollHeight <= container.clientHeight)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(container)
    observer.observe(content)
    return () => observer.disconnect()
  }, [containerRef, history?.nextCursor, transcript.messages.length])
  useLayoutEffect(() => {
    if (!history?.loading) restoreAfterPrepend()
  }, [history?.loading, restoreAfterPrepend])

  const loadEarlierMessages = () => {
    if (!history?.nextCursor || history.loading) return
    prepareForPrepend(messageListRef.current?.firstElementChild ?? null)
    appDispatch(AgentV2ConversationsActions.fetchOlderConversationHistory({ conversationId }))
  }

  // Draft links can point outside the newest page. Load back to the draft's chat turn so
  // its references/templates hydrate too, including when the chat panel is collapsed.
  useEffect(() => {
    if (!openMatterArtifactId || !history?.nextCursor || history.loading || history.error) return
    const draftTurnLoaded = transcript.messages.some((message) =>
      message.matterArtifactVersionIds?.some((id) => matterArtifactVersionsById[id]?.matter_artifact_id === openMatterArtifactId)
    )
    if (!draftTurnLoaded) appDispatch(AgentV2ConversationsActions.fetchOlderConversationHistory({ conversationId }))
  }, [appDispatch, conversationId, history, matterArtifactVersionsById, openMatterArtifactId, transcript.messages])

  /** Find the artifact's newest full button in the chat, if it has rendered. */
  const findLatestMatterArtifactButton = useCallback(
    (matterArtifactId: string) => {
      for (const matterArtifactVersionId of latestMatterArtifactVersionIds) {
        if (matterArtifactVersionsById[matterArtifactVersionId]?.matter_artifact_id !== matterArtifactId) continue
        return containerRef.current?.querySelector<HTMLElement>(`[data-matter-artifact-version-id="${CSS.escape(matterArtifactVersionId)}"]`) ?? null
      }
      return null
    },
    [containerRef, latestMatterArtifactVersionIds, matterArtifactVersionsById]
  )

  const viewCurrentMatterArtifact = useCallback(
    (matterArtifactVersion: MatterArtifactVersionRead) => {
      // Focus goes to the button the chat scrolls to, so keyboard and screen reader users land there too.
      findLatestMatterArtifactButton(matterArtifactVersion.matter_artifact_id)?.focus({ preventScroll: true })

      // The draft is already open, so there's nothing to open: just scroll the chat to reveal the artifact's button in its open state.
      if (openMatterArtifactId === matterArtifactVersion.matter_artifact_id) {
        setScrollToMatterArtifactVersionId(matterArtifactVersion.id)
        return
      }
      // Sent as navigation state so the scroll runs once the draft has opened.
      openMatterArtifactFromChat(matterArtifactVersion, { state: { scrollToMatterArtifactVersionId: matterArtifactVersion.id } })
    },
    [findLatestMatterArtifactButton, openMatterArtifactFromChat, openMatterArtifactId]
  )

  /**
   * Hold on to the artifact a scroll was requested for, since the request arrives well before the button it points at is in the DOM.
   *
   * The request comes as navigation state, set alongside the URL change that opens a draft.
   */
  useEffect(() => {
    if (!requestedScrollMatterArtifactVersionId) return
    setScrollToMatterArtifactVersionId(requestedScrollMatterArtifactVersionId)
  }, [requestedScrollMatterArtifactVersionId])

  /**
   * Scroll the chat to the artifact button the open artifact came from.
   *
   * Runs again as the transcript and the artifact versions hydrate, since the button is only in the DOM once both have arrived.
   */
  useEffect(() => {
    if (!scrollToMatterArtifactId) return
    const container = containerRef.current
    if (!container) return

    // An artifact edited in a later turn has a button per turn. Aim for its newest full button, or its last button while it
    // has none (only generating or outdated ones).
    const anchors = container.querySelectorAll(`[data-matter-artifact-anchor="${CSS.escape(scrollToMatterArtifactId)}"]`)
    const anchor = findLatestMatterArtifactButton(scrollToMatterArtifactId)?.closest('[data-matter-artifact-anchor]') ?? anchors[anchors.length - 1]
    if (!anchor) return

    setScrollToMatterArtifactVersionId(null)
    scrollElementIntoView(anchor)
  }, [containerRef, findLatestMatterArtifactButton, matterArtifactVersionsById, scrollElementIntoView, scrollToMatterArtifactId, transcript.messages])

  // Return to the bottom and resume following when the user starts a turn. The id is set at submit for both the
  // chat input and inline edits, then rewritten mid-turn, so only its arrival counts.
  const previousActiveAssistantMessageIdRef = useRef(transcript.activeAssistantMessageId)
  useEffect(() => {
    const previousActiveAssistantMessageId = previousActiveAssistantMessageIdRef.current
    previousActiveAssistantMessageIdRef.current = transcript.activeAssistantMessageId
    if (!transcript.activeAssistantMessageId || previousActiveAssistantMessageId) return
    scrollToBottom()
  }, [scrollToBottom, transcript.activeAssistantMessageId])

  // When a run ends, the focused Stop control can become the disabled Send control. Return focus
  // to the composer in that case, but never steal it from e.g. an open viewer (ENG-7077).
  useEffect(() => {
    if (isRunning === wasRunningRef.current) return
    const wasRunning = wasRunningRef.current
    wasRunningRef.current = isRunning
    if (!wasRunning || isRunning) return

    const focusWasOnRunControl = document.activeElement === document.body || document.activeElement === runControlButtonRef.current
    if (focusWasOnRunControl) textAreaRef.current?.focus()
  }, [isRunning])

  const handleStartOwnChat = () => {
    if (!conversationMatterId || isCreatingOwnChat) return

    setCreateOwnChatError(false)
    setIsCreatingOwnChat(true)
    void createNewAgentV2Chat(
      true,
      conversationMatterId,
      () => {
        setIsCreatingOwnChat(false)
      },
      (error) => {
        console.error('Error creating an Agent v2 chat from a shared conversation:', error)
        setCreateOwnChatError(true)
        setIsCreatingOwnChat(false)
      }
    )
  }

  const isEmptyNewChat = isConversationCreator && transcript.messages.length === 0

  // Opening a chat you can type in focuses its composer, unless a draft is open beside it (the draft is the focus there).
  // Touch devices are skipped, since focusing opens the on-screen keyboard over the page.
  const isTouchOnlyDevice = useIsTouchOnlyDevice()
  const isDraftOpenRef = useRef(false)
  isDraftOpenRef.current = Boolean(draftArtifactVersionId)
  useEffect(() => {
    const textArea = textAreaRef.current
    if (!isConversationCreator || isTouchOnlyDevice || isDraftOpenRef.current || !textArea) return
    textArea.focus()
    // Puts the cursor after any unsent text, so typing carries on from where it left off.
    textArea.setSelectionRange(textArea.value.length, textArea.value.length)
    textArea.scrollTop = textArea.scrollHeight
  }, [isConversationCreator, isTouchOnlyDevice])

  // FLIP transition for the first message: the input box re-renders from its centered
  // empty-state position straight to the docked bottom position, so translate it from where it was
  // at submit time down to its new place and fade the message area in (FE-555).
  useLayoutEffect(() => {
    if (isEmptyNewChat) return
    const fromRect = firstMessageFlipFromRectRef.current
    firstMessageFlipFromRectRef.current = null
    const inputBoxEl = chatInputBoxRef.current
    // element.animate is missing in jsdom; skipping there (and under reduced motion) keeps the
    // instant layout change as the fallback behavior.
    if (!fromRect || !inputBoxEl || typeof inputBoxEl.animate !== 'function') return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const deltaY = fromRect.top - inputBoxEl.getBoundingClientRect().top
    if (deltaY !== 0) {
      inputBoxEl.animate([{ transform: `translateY(${deltaY}px)` }, { transform: 'translateY(0)' }], { duration: 300, easing: 'ease-in-out' })
    }
    containerRef.current?.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 300, easing: 'ease-in' })
  }, [isEmptyNewChat, containerRef])

  return (
    <div className="agent-v2-chat-window relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden" {...chatFileDragHandlers}>
      {/* Explicit keys on these top-level siblings so React tracks each by identity, not position — letting this one unmount when empty without disturbing (and remounting) ChatInputBox below. */}
      {!isEmptyNewChat && (
        <div key="message-scroll-area" className="relative min-h-0 flex-1">
          <div
            className="agent-v2-message-scroll chat-scroll-bottom-fade h-full overflow-y-auto overscroll-y-contain px-2"
            onScroll={(event) => {
              handleScroll()
              const container = event.currentTarget
              // Start three viewports ahead to leave time for the request and rendering while scrolling.
              if (
                !history?.error &&
                container.scrollTop < container.clientHeight * 3 &&
                container.scrollTop + container.clientHeight < container.scrollHeight
              ) {
                loadEarlierMessages()
              }
            }}
            onWheel={(event) => {
              handleUserScrollInput()
              if (event.deltaY < 0 && event.currentTarget.scrollTop < event.currentTarget.clientHeight * 3 && !history?.error) loadEarlierMessages()
            }}
            onTouchMove={handleUserScrollInput}
            ref={containerRef}
          >
            {/* With no bottom room while an answer streams, its newest line reaches into the fade above the
                composer; once it finishes, the full fade height keeps the last line clear of it. */}
            <div className={`mx-auto flex min-h-full w-full max-w-3xl flex-col pt-4 ${isRunning ? 'pb-0' : 'pb-8'}`} ref={contentRef}>
              {history?.nextCursor && !history.error && (
                <div className="flex h-10 shrink-0 items-center justify-center">
                  {history.loading ? (
                    <p role="status" className="text-sm text-brand-neutral-600">
                      Loading earlier messages…
                    </p>
                  ) : historyFitsViewport ? (
                    <Button variant="plain" color="neutral" size="sm" onClick={loadEarlierMessages}>
                      Load earlier messages
                    </Button>
                  ) : null}
                </div>
              )}
              {history?.error && (
                <div className="flex flex-col items-center gap-2 pb-4">
                  <p role="alert" className="text-sm text-brand-neutral-600">
                    Could not load earlier messages.
                  </p>
                  <Button variant="plain" color="neutral" size="sm" onClick={loadEarlierMessages}>
                    Retry loading earlier messages
                  </Button>
                </div>
              )}
              <div className="agent-v2-message-list flex-1 space-y-4" ref={messageListRef}>
                {visibleMessages.map((message) => (
                  <AgentV2ChatMessage
                    key={message.id}
                    conversationId={conversationId}
                    message={message}
                    onFileLinkClick={openFileLink}
                    renderLink={renderLink}
                    renderResponseFooter={renderResponseFooter}
                    onFollowUpQuestionClick={handleFollowUpQuestionClick}
                    onFeedbackChange={handleFeedbackChange}
                    onMatterArtifactCancel={sendStop}
                    onMatterArtifactView={openMatterArtifactFromChat}
                    onMatterArtifactViewCurrent={viewCurrentMatterArtifact}
                    progressRunning={message.id === transcript.activeAssistantMessageId && isRunning}
                    readOnly={!isConversationCreator}
                    onDownloadConversation={onDownloadConversation}
                    responsePresentation={responsePresentation}
                    compactProgress={compactProgress}
                    formatUserMessage={formatUserMessage}
                    showReferenceList={showReferenceList}
                    addedFiles={turnAddedFilesByMessageId[message.id]}
                  />
                ))}
              </div>
            </div>
          </div>
          {showScrollToBottomButton && <ScrollToBottomButton onClick={scrollToBottom} />}
        </div>
      )}
      {isEmptyNewChat && openTableId && (
        <div key="empty-state" className="agent-v2-empty-state-region flex min-h-0 flex-1 flex-col items-center justify-center overflow-y-auto px-4 py-6">
          {emptyStateContent ?? (
            <div className="flex w-full max-w-3xl flex-col items-center gap-4 text-center">
              <img alt="" className="size-6" src={kDefaultFaviconPath} />
              <h1 className="font-rethink-sans text-lg font-semibold text-brand-neutral-900">{emptyStateHeading}</h1>
              {emptyStateActions}
            </div>
          )}
        </div>
      )}

      <div
        key="composer-region"
        className={`agent-v2-composer-region ${
          isEmptyNewChat && !openTableId ? 'flex flex-1 flex-col items-center justify-center gap-6 px-2 pb-32' : 'shrink-0 px-2 pb-4'
        }`}
        data-empty={isEmptyNewChat}
      >
        {isEmptyNewChat &&
          !openTableId &&
          (emptyStateContent ?? (
            <div key="empty-state-heading" className="flex w-full max-w-3xl flex-col gap-4">
              <div className="flex items-center gap-2">
                <img alt="" className="size-6" src={kDefaultFaviconPath} />
                <h1 className="font-rethink-sans text-2xl font-semibold text-brand-neutral-900">{emptyStateHeading}</h1>
              </div>
              {emptyStateActions}
            </div>
          ))}
        {/* Sits above the composer so a reader meets the warning before acting on a citation. */}
        <AgentV2StaleLawBanner hasStaleLaw={hasStaleLaw} />
        {isConversationCreator ? (
          composerUnavailableContent ?? (
            <div
              key="chat-input-box"
              className={`agent-v2-input-wrapper ${isEmptyNewChat && !openTableId ? 'w-full max-w-3xl' : 'pt-2'}`}
              ref={chatInputBoxRef}
            >
              {composerActions}
              <ChatWindowUserFilesUpload ref={chatWindowUserFilesUploadRef} conversationId={conversationId} matterId={conversationMatterId ?? null} />
              <ChatWindowUserFilesUpload
                ref={chatWindowWritingSampleUploadRef}
                conversationId={conversationId}
                matterId={conversationMatterId ?? null}
                uploadTarget="writing-sample"
              />
              <FileUploadContext.Provider
                value={{
                  openSystemUserFilesFileSelector,
                  openSystemWritingSampleFileSelector,
                  cancelUserFileUpload,
                  userFilesUploadDisabled: !!userFilesUploadDisabledReason,
                  userFilesUploadDisabledReason,
                }}
              >
                <AgentV2ChatComposer
                  conversationId={conversationId}
                  conversationSubmitBlockedReason={conversationSubmitBlockedReason}
                  disableActions={isFirstLoadPending || preparingRequest}
                  errorMessage={prepareError ?? transcript.errorMessage}
                  hasAttachedSkills={hasAttachedSkills}
                  hasUnreadyUserFiles={hasUnreadyUserFiles}
                  isRecovering={isRecovering}
                  recoveryStartedAfterDetachment={transportIsDetached}
                  isRunning={isRunning}
                  isStopping={isStopping}
                  matterId={conversation?.matter_id ?? matterId ?? null}
                  matterSubmitBlockedReason={matterSubmitBlockedReason}
                  onStop={sendStop}
                  onSubmitText={submitText}
                  runControlButtonRef={runControlButtonRef}
                  textAreaRef={textAreaRef}
                  placeholder={inputPlaceholder}
                  onOpenMatterSettings={onOpenMatterSettings}
                  onOpenGlobalSettings={onOpenGlobalSettings}
                  showVoiceInput={showVoiceInput}
                />
              </FileUploadContext.Provider>
            </div>
          )
        ) : (
          <ViewOnlyChatNotice
            key="view-only-chat-notice"
            ownerDisplayName={creatorDisplayName}
            disabled={isCreatingOwnChat}
            errorMessage={createOwnChatError ? 'Error creating chat. Please try again.' : null}
            onStartOwnChat={handleStartOwnChat}
          />
        )}
      </div>

      <DocxExportAnnouncer messages={transcript.messages} />

      {isDraggingFilesOverChat && (
        <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center rounded-t-[6px] border-l-[1.5px] border-r-[1.5px] border-t-[1.5px] border-dashed border-brand-400 bg-white/90">
          <div className="rounded-full border border-brand-200 bg-brand-50 px-4 py-3">
            <DropFilesToUploadLabel />
          </div>
        </div>
      )}
    </div>
  )
}
